// Client-side guard for the /admin/* tree. While the session lookup is
// in flight the admin shell stays hidden behind a centered "verifying
// access" affordance so the user gets immediate feedback instead of a
// flash of admin chrome followed by 403'd data calls. Non-admins land
// on an explicit denial state (or sign-in prompt if unauthenticated)
// rather than seeing a broken-looking admin page.
import { Loader2, ShieldAlert } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useLocation } from 'react-router';

import { Button } from '@/components/ui/button';
import Link from '@/components/ui/link';
import { useSession } from '@/lib/auth-client';

const ADMIN_ROLES = new Set(['admin', 'developer']);

function CenteredCard({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-2xl border border-border/60 bg-card/80 p-6 text-center shadow-xl backdrop-blur">
        <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          {icon}
        </div>
        <h2 className="text-base font-semibold tracking-tight text-foreground">
          {title}
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}

export function AdminGate({ children }: { children: ReactNode }): ReactElement {
  const location = useLocation();
  const session = useSession();
  if (location.pathname === '/admin/emergency') return <>{children}</>;

  if (session.isPending) {
    return (
      <CenteredCard
        icon={<Loader2 className="h-5 w-5 animate-spin" />}
        title="Verifying access"
        description="Checking your admin permissions…"
      />
    );
  }

  const user = session.data?.user;
  if (!user) {
    return (
      <CenteredCard
        icon={<ShieldAlert className="h-5 w-5" />}
        title="Sign in required"
        description="The admin console is only available to signed-in administrators."
        action={
          <Button asChild className="w-full">
            <Link href="/login?next=/admin">Sign in</Link>
          </Button>
        }
      />
    );
  }

  const role = typeof user.role === 'string' ? user.role : '';
  if (!ADMIN_ROLES.has(role)) {
    return (
      <CenteredCard
        icon={<ShieldAlert className="h-5 w-5" />}
        title="Access denied"
        description="Your account doesn't have admin privileges. If you believe this is a mistake, contact a developer."
        action={
          <Button asChild variant="outline" className="w-full">
            <Link href="/">Back to home</Link>
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}

export default AdminGate;
